import { Button, DatePicker, message, Table, Tag } from "antd";
import React, { useState } from "react";
import { DownloadOutlined } from "@ant-design/icons";
import { useSelector } from "react-redux";
import { selectBaseUrl, selectProdTestUrl } from "../../redux/slices/envSlice";
import moment from "moment";
import dayjs from "dayjs";
import { token } from "../../constants/Constants";
import { get_test_report } from "../../endpoints/API";

const PhleboIncentiveReport = () => {
  const baseUrl = useSelector(selectBaseUrl);
  const prodTestUrl = useSelector(selectProdTestUrl);
  const { RangePicker } = DatePicker;
  const [selectedDates, setSelectedDates] = useState([]);
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const fetchReportData = async (dates, currentPage = page, currentPageSize = pageSize) => {
    try {
      const response = await get_test_report(dates?.[0], dates?.[1], currentPage, currentPageSize);
      if (response.status !== 200) throw new Error("Network response was not ok");
      const resData = response.data;
      setData(resData.results || []);
      setTotal(resData.count || 0);
    } catch (error) {
      setData([]);
      message.error(error?.response?.data?.error || "Error fetching report data");
    }
  };

  const handleDateChange = (value, dateString) => {
    setSelectedDates(dateString);
    setPage(1);
    fetchReportData(dateString, 1, pageSize);
  };

  const handleDownload = async () => {
    try {
      const response = await get_test_report(selectedDates?.[0], selectedDates?.[1], 1, total || 10000);
      const allData = response?.data?.results || [];
      const cols = [
        { title: "Order Number", dataIndex: "order_number" },
        { title: "Scheduled Date", dataIndex: "schedule_date" },
        { title: "Patient Name", dataIndex: "family_member_name" },
        { title: "Test ID", dataIndex: "test_id" },
        { title: "Test Name", dataIndex: "test_name" },
        { title: "Test Amount", dataIndex: "test_amount" },
        { title: "Wellness Executive", dataIndex: "wellness_executive_name" },
        { title: "Lab Name", dataIndex: "lab" },
      ];
      const csvContent = [
        cols.map((c) => c.title).join(","),
        ...allData.map((row) =>
          cols.map((c) => {
            if (c.dataIndex === "schedule_date" && row[c.dataIndex])
              return moment(row[c.dataIndex]).format("DD/MM/YYYY hh:mm A");
            return row[c.dataIndex] ?? "";
          }).join(",")
        ),
      ].join("\n");
      const blob = new Blob([csvContent], { type: "text/csv" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = "PhleboIncentiveReport.csv";
      link.click();
    } catch (error) {
      message.error("Failed to download report");
    }
  };

  const columns = [
    { title: "Order Number", dataIndex: "order_number", key: "order_number", width: 120 },
    { title: "Scheduled Date", dataIndex: "schedule_date", key: "schedule_date", width: 150, render: (t) => t ? moment(t).format("DD/MM/YYYY hh:mm A") : "-" },
    { title: "Patient Name", dataIndex: "family_member_name", key: "family_member_name", width: 130 },
    { title: "Test ID", dataIndex: "test_id", key: "test_id", width: 90, render: (t) => t || "-" },
    { title: "Test Name", dataIndex: "test_name", key: "test_name", width: 150 },
    { title: "Test Amount", dataIndex: "test_amount", key: "test_amount", width: 110 },
    { title: "Wellness Executive", dataIndex: "wellness_executive_name", key: "wellness_executive_name", width: 150, render: (t) => t || "-" },
    { title: "Lab Name", dataIndex: "lab", key: "lab", width: 120 },
  ];

  return (
    <div style={{ padding: "8px" }}>
      <div className="flex flex-col md:flex-row md:items-center gap-2 mb-2">
        <span className="font-semibold whitespace-nowrap">Total: <Tag>{total}</Tag></span>
        <div className="w-full md:flex-1 md:max-w-xs">
          <RangePicker
            format="YYYY-MM-DD"
            onChange={handleDateChange}
            disabledDate={(current) => current && current > dayjs().endOf("day")}
            style={{ width: "100%" }}
          />
        </div>
        <Button type="primary" onClick={handleDownload} disabled={!data.length} className="w-full md:w-auto">
          Download Report <DownloadOutlined />
        </Button>
      </div>
      <div style={{ overflowX: "auto", marginTop: 16 }}>
        <Table
          columns={columns}
          dataSource={data}
          rowKey={(record, i) => `${record.order_number}-${i}`}
          scroll={{ x: "max-content" }}
          size="small"
          pagination={{
            current: page,
            pageSize,
            total,
            showSizeChanger: true,
            responsive: true,
            size: "small",
            onChange: (newPage, newPageSize) => {
              setPage(newPage);
              setPageSize(newPageSize);
              fetchReportData(selectedDates, newPage, newPageSize);
            },
          }}
        />
      </div>
    </div>
  );
};

export default PhleboIncentiveReport;